import { 
  collection, doc, setDoc, deleteDoc, onSnapshot, 
  query, where, increment, updateDoc, getDocs 
} from "firebase/firestore";
import { db } from "../firebase";
import { Appliance, User, WishlistEntry, cleanFirestoreData } from "../types";

const LOCAL_STORAGE_KEY = "rentlow_user_wishlist";
const GUEST_ID_KEY = "rentlow_guest_wishlist_id";

export function getGuestWishlistId(): string {
  if (typeof window === "undefined") return "guest_ssr";
  try {
    let guestId = localStorage.getItem(GUEST_ID_KEY);
    if (!guestId) {
      guestId = `guest_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      localStorage.setItem(GUEST_ID_KEY, guestId);
    }
    return guestId;
  } catch (e) {
    return "guest_fallback";
  }
}

export function getEffectiveUserId(currentUser: User | null): string {
  return currentUser?.uid || getGuestWishlistId();
}

export function getLocalWishlist(): WishlistEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (err) {
    console.warn("Failed to load local wishlist:", err);
    return [];
  }
}

export function saveLocalWishlist(items: WishlistEntry[]): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(items));
  } catch (err) {
    console.warn("Failed to save local wishlist:", err);
  }
}

export function isProductWishlisted(applianceId: string, items: WishlistEntry[]): boolean {
  return items.some(i => i.applianceId === applianceId);
}

export async function toggleWishlistProduct(
  appliance: Appliance,
  currentUser: User | null,
  currentItems: WishlistEntry[]
): Promise<{ newItems: WishlistEntry[]; isAdded: boolean }> {
  const userId = getEffectiveUserId(currentUser);
  const existingIdx = currentItems.findIndex(i => i.applianceId === appliance.id);
  const isAlreadySaved = existingIdx >= 0;

  const docId = `${userId}_${appliance.id}`;
  const wishlistDocRef = doc(db, "wishlists", docId);
  const applianceDocRef = doc(db, "appliances", appliance.id);

  if (isAlreadySaved) {
    // Remove from wishlist
    const nextItems = currentItems.filter(i => i.applianceId !== appliance.id);
    saveLocalWishlist(nextItems);

    // Asynchronous background firestore sync
    (async () => {
      try {
        await deleteDoc(wishlistDocRef);
        try {
          await updateDoc(applianceDocRef, {
            wishlistCount: increment(-1)
          });
        } catch (e) {
          // Document might not have the field or not exist, non-critical
        }
      } catch (err) {
        console.warn("Firestore wishlist deletion error:", err);
      }
    })();

    return { newItems: nextItems, isAdded: false };
  } else {
    // Add to wishlist
    const safeImages = Array.isArray(appliance.images) && appliance.images.length > 0
      ? appliance.images
      : typeof appliance.images === "string" && appliance.images
      ? [appliance.images]
      : ["https://images.unsplash.com/photo-1571175432267-efb0258593a6?auto=format&fit=crop&w=600&q=80"];

    const newEntry: WishlistEntry = {
      id: docId,
      userId,
      userPhone: currentUser?.phone || "",
      userName: currentUser?.name || "Guest Visitor",
      applianceId: appliance.id,
      applianceName: appliance.name || "Appliance",
      categoryId: appliance.categoryId || "",
      rentalPrice: appliance.rentalPrice || 0,
      securityDeposit: appliance.securityDeposit || 0,
      imageUrl: safeImages[0],
      isUpcoming: Boolean(appliance.isUpcoming),
      isOutOfStock: Boolean(appliance.isOutOfStock),
      createdAt: new Date().toISOString()
    };

    const nextItems = [newEntry, ...currentItems.filter(i => i.applianceId !== appliance.id)];
    saveLocalWishlist(nextItems);

    // Asynchronous background firestore sync
    (async () => {
      try {
        await setDoc(wishlistDocRef, cleanFirestoreData(newEntry));
        try {
          await updateDoc(applianceDocRef, {
            wishlistCount: increment(1)
          });
        } catch (e) {
          try {
            await updateDoc(applianceDocRef, {
              wishlistCount: 1
            });
          } catch (subErr) {
            // ignore
          }
        }
      } catch (err) {
        console.warn("Firestore wishlist write error:", err);
      }
    })();

    return { newItems: nextItems, isAdded: true };
  }
}

export async function removeWishlistItem(
  applianceId: string,
  currentUser: User | null,
  currentItems: WishlistEntry[]
): Promise<WishlistEntry[]> {
  const userId = getEffectiveUserId(currentUser);
  const nextItems = currentItems.filter(i => i.applianceId !== applianceId);
  saveLocalWishlist(nextItems);

  const docId = `${userId}_${applianceId}`;
  (async () => {
    try {
      await deleteDoc(doc(db, "wishlists", docId));
      try {
        await updateDoc(doc(db, "appliances", applianceId), {
          wishlistCount: increment(-1)
        });
      } catch (e) {}
    } catch (err) {
      console.warn("Firestore wishlist removal error:", err);
    }
  })();

  return nextItems;
}

export async function clearAllWishlistItems(
  currentUser: User | null,
  currentItems: WishlistEntry[]
): Promise<void> {
  const userId = getEffectiveUserId(currentUser);
  saveLocalWishlist([]);

  // Clear in firestore asynchronously
  (async () => {
    const promises = currentItems.map(async (item) => {
      const docId = `${userId}_${item.applianceId}`;
      try {
        await deleteDoc(doc(db, "wishlists", docId));
        await updateDoc(doc(db, "appliances", item.applianceId), {
          wishlistCount: increment(-1)
        });
      } catch (e) {}
    });

    await Promise.allSettled(promises);
  })();
}

/**
 * Automatically merges local/guest wishlist items with authenticated user profile
 * when a user logs in, ensuring no saved products are lost.
 */
export async function syncUserWishlistOnAuthChange(
  currentUser: User | null,
  currentLocal: WishlistEntry[]
): Promise<WishlistEntry[]> {
  if (!currentUser?.uid) {
    return currentLocal;
  }

  try {
    const userWishlistQuery = query(
      collection(db, "wishlists"),
      where("userId", "==", currentUser.uid)
    );
    const snapshot = await getDocs(userWishlistQuery);
    const remoteItems: WishlistEntry[] = [];
    snapshot.forEach(docSnap => {
      remoteItems.push(docSnap.data() as WishlistEntry);
    });

    // Map by applianceId
    const mergedMap = new Map<string, WishlistEntry>();
    remoteItems.forEach(item => {
      mergedMap.set(item.applianceId, item);
    });

    // Merge any guest items that were saved locally
    const guestDocIdsToMigrate: string[] = [];
    for (const item of currentLocal) {
      if (!mergedMap.has(item.applianceId)) {
        const migratedDocId = `${currentUser.uid}_${item.applianceId}`;
        const updatedEntry: WishlistEntry = {
          ...item,
          id: migratedDocId,
          userId: currentUser.uid,
          userPhone: currentUser.phone || item.userPhone || "",
          userName: currentUser.name || item.userName || ""
        };
        mergedMap.set(item.applianceId, updatedEntry);

        // Upload migrated entry to Firestore
        try {
          await setDoc(doc(db, "wishlists", migratedDocId), cleanFirestoreData(updatedEntry));
          // If the item had a different guest doc ID, clean it up
          if (item.id && item.id !== migratedDocId && item.id.startsWith("guest_")) {
            guestDocIdsToMigrate.push(item.id);
          }
        } catch (e) {
          console.warn("Error migrating guest wishlist item to user:", e);
        }
      }
    }

    // Clean up old guest documents
    if (guestDocIdsToMigrate.length > 0) {
      guestDocIdsToMigrate.forEach(async (oldId) => {
        try {
          await deleteDoc(doc(db, "wishlists", oldId));
        } catch (e) {}
      });
    }

    const mergedList = Array.from(mergedMap.values());
    saveLocalWishlist(mergedList);
    return mergedList;
  } catch (err) {
    console.warn("Failed to sync user wishlist on auth change:", err);
    return currentLocal;
  }
}

/**
 * Subscribes to real-time updates for an individual user's wishlist
 */
export function subscribeUserWishlist(
  userId: string,
  onUpdate: (items: WishlistEntry[]) => void
): () => void {
  if (!userId) return () => {};
  try {
    const q = query(collection(db, "wishlists"), where("userId", "==", userId));
    return onSnapshot(q, (snapshot) => {
      const list: WishlistEntry[] = [];
      snapshot.forEach(docSnap => {
        list.push(docSnap.data() as WishlistEntry);
      });
      if (list.length > 0) {
        saveLocalWishlist(list);
        onUpdate(list);
      }
    }, (err) => {
      console.warn("User wishlist subscription error:", err);
    });
  } catch (e) {
    console.warn("Failed to attach user wishlist listener:", e);
    return () => {};
  }
}

/**
 * Admin subscription to track overall demand
 */
export function subscribeAllWishlists(onUpdate: (items: WishlistEntry[]) => void): () => void {
  try {
    const q = collection(db, "wishlists");
    return onSnapshot(q, (snapshot) => {
      const list: WishlistEntry[] = [];
      snapshot.forEach(docSnap => {
        list.push(docSnap.data() as WishlistEntry);
      });
      onUpdate(list);
    }, (err) => {
      console.warn("Wishlist subscription error:", err);
    });
  } catch (e) {
    console.warn("Failed to attach wishlist listener:", e);
    return () => {};
  }
}
